import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DialogModal from '../../DialogModal/DialogModal';
import SectionWrapper from '../../CardRow/SectionWrapper';
import DetailWrapper from '../../CardRow/DetailWrapper';
import CustomTextField from '../../CustomizedComponents/CustomTextField';
import useAppContext from '../../../hooks/context/useAppContext';
import ButtonWrapper from '../../Button/ButtonWrapper';
import DefaultButton from '../../Button/DefaultButton';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

const getOptions = (option) => {
  return option.map((item) => (
    <option key={item.id} value={item.id}>
      {item.name ? item.name : item.idExternal}
    </option>
  ));
};

const BasicInformation = ({ transaction, handlers, editable, setEditable, isNew, handler, idPanel, setOpen, loading }) => {
  const {
    id,
    state,
    settleState,
    customersEnum,
    customerId,
    contractId,
    contractsEnum,
    commodity,
    user,
    createdAt,
    deleteError,
    deleteLoading,
    deleteData,
  } = transaction;

  const { setState, setUser, setContractId, setCustomerId, setCreatedAt, sendHandler, removeTransaction } = handlers;
  const { handleSubmit, register, errors } = useForm();
  const { getUser } = useAppContext();
  const history = useHistory();
  const loggedUser = getUser();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (!user && loggedUser) {
      setUser(loggedUser);
    }
  }, [loggedUser, user, setUser]);

  useEffect(() => {
    if (!deleteError && !deleteLoading && deleteData) {
      setOpen(true);
      history.push(`/transactions`);
    }
  }, [deleteError, deleteLoading, deleteData, history, setOpen]);

  const handleChange = (idButton) => {
    if (!isNew) {
      setEditable(!editable);
      if (idButton !== 'cancel' && idButton !== 'delete') {
        setOpen(true);
        sendHandler();
      } else if (idButton === 'delete') {
        removeTransaction();
      }
    } else {
      handler(idPanel, true);
    }
  };

  const intl = useIntl();
  const settledTitle = 'app.transaction.settled';
  const customerTitle = 'app.organization';
  const contractTitle = 'app.contracts.contractNumber';
  const userTitle = 'app.transaction.author';
  const userIDTitle = 'app.transaction.userID';
  const commodityIDTitle = 'app.transactions.commodity';
  const createdAtTitle = 'app.transactions.createdAt';
  const dataMissing = intl.formatMessage({ id: 'app.data.missing' });
  const cancel = 'app.buttons.cancel';
  const save = 'app.buttons.save';
  const continueTitle = 'app.buttons.continue';
  const deleteTitle = 'app.buttons.delete';
  const dialogTitle = 'app.transaction.confirmation';

  return (
    <>
      <DetailWrapper>
        <SectionWrapper>
          <form onSubmit={handleSubmit(handleChange)}>
            <CustomTextField
              id="1"
              name={'state'}
              onChange={(event) => {
                setState(event.target.value);
              }}
              select
              title={settledTitle}
              value={state}
              disabled={!editable}
              SelectProps={{
                native: true,
              }}
              variant="outlined">
              {getOptions(settleState)}
            </CustomTextField>

            <CustomTextField
              id="2"
              name={'customerId'}
              onChange={(event) => {
                setCustomerId(event.target.value);
              }}
              select
              title={customerTitle}
              value={customerId}
              disabled={isNew ? false : true}
              SelectProps={{
                native: true,
              }}
              variant="outlined"
              inputRef={register({
                required: dataMissing,
              })}
              helperText={errors.customerId ? errors.customerId.message : ''}
              error={!!errors.customerId}>
              <option value={null} />
              {getOptions(customersEnum)}
            </CustomTextField>
            {contractsEnum && isNew && (
              <CustomTextField
                id="3"
                name={'contractId'}
                onChange={(event) => {
                  setContractId(event.target.value);
                }}
                select
                title={contractTitle}
                value={contractId}
                disabled={!editable}
                SelectProps={{
                  native: true,
                }}
                variant="outlined"
                inputRef={register({
                  required: dataMissing,
                })}
                helperText={errors?.contractId?.message}
                error={!!errors?.contractId}>
                <option value={null} />
                {getOptions(contractsEnum)}
              </CustomTextField>
            )}
            <CustomTextField
              id="4"
              name={'createdAt'}
              onChange={(event) => {
                event.stopPropagation();
                setCreatedAt(new Date(event.target.value));
              }}
              title={createdAtTitle}
              value={format(new Date(createdAt), 'yyyy-MM-dd')}
              variant="outlined"
              inputRef={register({
                required: dataMissing,
              })}
              type="date"
              disabled={!editable}
            />
            <BasicInformationDetailTextFields
              userName={`${user?.firstname} ${user?.surname}`}
              userNameTitle={userTitle}
              userID={`${user?.id}`}
              userIDTitle={userIDTitle}
              commodity={commodity}
              commodityIDTitle={commodityIDTitle}
              createdAt={createdAt}
              createdAtTitle={createdAtTitle}
            />
            <ButtonWrapper>
              <DefaultButton
                id={'cancel'}
                primary={false}
                disabled={!editable}
                title={cancel}
                onClick={(event) => {
                  handleChange(event.currentTarget.id);
                }}
              />
              {id !== 'new' && (
                <DefaultButton
                  id={'delete'}
                  primary={false}
                  title={deleteTitle}
                  disabled={!editable}
                  onClick={() => setIsModalOpen(true)}
                  loaders={[deleteLoading]}
                />
              )}
              <DefaultButton primary={true} disabled={!editable} type="submit" title={isNew ? continueTitle : save} loaders={loading} />
            </ButtonWrapper>
          </form>
        </SectionWrapper>
        <DialogModal
          isModalOpen={isModalOpen}
          editable={editable}
          confirmAction={removeTransaction}
          cancelAction={() => setIsModalOpen(false)}
          dialogTitle={dialogTitle}
          confirmBtnTitle={deleteTitle}
          cancelBtnTitle={cancel}
        />
      </DetailWrapper>
    </>
  );
};

BasicInformation.propTypes = {
  transaction: PropTypes.object,
  handlers: PropTypes.object,
  editable: PropTypes.bool,
  isNew: PropTypes.bool,
  register: PropTypes.any,
  errors: PropTypes.object,
  setEditable: PropTypes.func,
  handler: PropTypes.func,
  idPanel: PropTypes.any,
  setOpen: PropTypes.func,
  loading: PropTypes.any,
};

const BasicInformationDetailTextFields = ({ userName, userNameTitle, userID, userIDTitle, commodity, commodityIDTitle }) => {
  const fields = [
    {
      id: '2',
      name: 'commodity',
      title: commodityIDTitle,
      value: commodity,
      disabled: true,
      variant: 'outlined',
    },
    {
      id: '3',
      name: 'user',
      title: userNameTitle,
      value: userName,
      disabled: true,
      variant: 'outlined',
    },
    {
      id: '4',
      name: 'userID',
      title: userIDTitle,
      value: userID,
      disabled: true,
      variant: 'outlined',
    },
  ];

  return (
    <>
      {fields.map((field) => (
        <CustomTextField key={field.id} {...field} />
      ))}
    </>
  );
};

BasicInformationDetailTextFields.propTypes = {
  userName: PropTypes.any,
  userNameTitle: PropTypes.any,
  userID: PropTypes.any,
  userIDTitle: PropTypes.any,
  commodity: PropTypes.any,
  commodityIDTitle: PropTypes.any,
  createdAt: PropTypes.any,
  createdAtTitle: PropTypes.any,
};

export default BasicInformation;
